import { useRef, useEffect } from 'react';
import styles from './NavigationMenu.module.css';
import type { Filter } from '../types/filter';

interface Menu {
  id: string;
  name: string;
}

interface NavigationMenuProps {
  menus: Menu[];
  selectedMenuId: string | null;
  loading: boolean;
  onMenuSelect: (menuId: string) => void;
  filters?: Filter[];
  onFiltersClick?: () => void;
  selectedFiltersCount?: number;
}

export default function NavigationMenu({ 
  menus, 
  selectedMenuId, 
  loading, 
  onMenuSelect,
  filters,
  onFiltersClick,
  selectedFiltersCount
}: NavigationMenuProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const menuButtonsRef = useRef<Map<string, HTMLButtonElement>>(new Map());

  useEffect(() => {
    if (selectedMenuId && scrollContainerRef.current) {
      const selectedButton = menuButtonsRef.current.get(selectedMenuId);
      if (selectedButton) {
        const container = scrollContainerRef.current;
        const containerWidth = container.offsetWidth;
        const buttonLeft = selectedButton.offsetLeft;
        const buttonWidth = selectedButton.offsetWidth;
        const scrollLeft = buttonLeft - (containerWidth / 2) + (buttonWidth / 2);
        
        container.scrollTo({
          left: scrollLeft,
          behavior: 'smooth'
        });
      }
    }
  }, [selectedMenuId]);

  const handleMenuClick = (menuId: string) => {
    onMenuSelect(menuId);
  };

  return (
    <div className="mb-0">
      <div className="flex items-center py-2 px-6">
        <div ref={scrollContainerRef} className="flex items-center space-x-8 overflow-x-auto scrollbar-hide flex-1">
          {menus.map((menu) => (
            <button
              key={menu.id}
              ref={(el) => {
                if (el) {
                  menuButtonsRef.current.set(menu.id, el);
                } else {
                  menuButtonsRef.current.delete(menu.id);
                }
              }}
              onClick={() => handleMenuClick(menu.id)}
              disabled={loading}
              className={`whitespace-nowrap transition-colors ${styles.menuButton} ${
                selectedMenuId === menu.id
                  ? styles.menuSelected
                  : 'text-gray-300 hover:text-white'
              } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {menu.name}
            </button>
          ))}
        </div>
        
        {filters && filters.length > 0 && (
          <>
            <div className="w-px bg-gray-400 opacity-30 mx-4 self-stretch"></div>
            <button
              onClick={onFiltersClick}
              disabled={loading}
              className={`flex items-center gap-2 whitespace-nowrap font-semibold text-lg transition-colors text-gray-300 hover:text-white ${
                loading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {selectedFiltersCount !== undefined && selectedFiltersCount > 0 && (
                <span className={styles.filterCount}>({selectedFiltersCount})</span>
              )}
              <img src="/filters@2x.png" alt="Filtri" className="w-5 h-5" />
            </button>
          </>
        )}
      </div>
      <div className="border-t border-white opacity-20" style={{borderWidth: '0.5px'}}></div>
    </div>
  );
}